/** @format */

import { motion } from "framer-motion";
import { X } from "lucide-react"; // ✅ bouton fermeture
import { useState } from "react";
import { FaShoppingBag, FaStar, FaStore, FaTruck } from "react-icons/fa";
import { MdLocalOffer } from "react-icons/md";

const badges = [
  { icon: <FaStore size={14} />, label: "Multi-vendeurs" },
  { icon: <FaTruck size={14} />, label: "Livraison rapide" },
  { icon: <MdLocalOffer size={14} />, label: "Meilleures offres" },
  { icon: <FaStar size={14} />, label: "100% Fiable" },
];

const floatingItems = [
  { emoji: "👗", x: "8%", y: "18%", delay: 0 },
  { emoji: "📱", x: "80%", y: "12%", delay: 0.3 },
  { emoji: "👟", x: "75%", y: "65%", delay: 0.6 },
  { emoji: "🛍️", x: "5%", y: "70%", delay: 0.9 },
  { emoji: "💄", x: "50%", y: "5%", delay: 1.2 },
];

export default function GobyMallPromoCard({ onClose }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div className='fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'>
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className='relative w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl cursor-pointer select-none'
        style={{
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        }}
      >
        {/* ✅ BOUTON FERMETURE */}
        <button
          onClick={onClose}
          className='absolute top-3 right-3 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 backdrop-blur-md transition'
        >
          <X size={18} />
        </button>

        {/* Background glow */}
        <motion.div
          animate={{ scale: hovered ? 1.3 : 1, opacity: hovered ? 0.6 : 0.3 }}
          transition={{ duration: 0.6 }}
          className='absolute inset-0 rounded-full blur-3xl'
          style={{
            background: "radial-gradient(circle, #e94560 0%, transparent 70%)",
            top: "30%",
            left: "20%",
            width: "60%",
            height: "60%",
          }}
        />
        <motion.div
          animate={{ scale: hovered ? 1.2 : 1, opacity: hovered ? 0.4 : 0.2 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className='absolute inset-0 rounded-full blur-2xl'
          style={{
            background: "radial-gradient(circle, #f5a623 0%, transparent 70%)",
            top: "-10%",
            right: "-10%",
            width: "50%",
            height: "50%",
          }}
        />

        {/* Floating emojis */}
        {floatingItems.map((item, i) => (
          <motion.span
            key={i}
            className='absolute text-xl pointer-events-none'
            style={{ left: item.x, top: item.y }}
            animate={{ y: [0, -10, 0], rotate: [0, 8, -8, 0] }}
            transition={{
              repeat: Infinity,
              duration: 3 + i * 0.4,
              delay: item.delay,
              ease: "easeInOut",
            }}
          >
            {item.emoji}
          </motion.span>
        ))}

        {/* Content */}
        <div className='relative z-10 p-7 flex flex-col items-center text-center gap-5'>
          {/* Logo / Brand */}
          <motion.div
            animate={{ scale: hovered ? 1.08 : 1 }}
            transition={{ duration: 0.4 }}
            className='flex flex-col items-center gap-1'
          >
            <div className='flex items-center gap-2'>
              <motion.div
                animate={{ rotate: hovered ? 360 : 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className='w-10 h-10 rounded-xl flex items-center justify-center'
                style={{
                  background: "linear-gradient(135deg, #e94560, #f5a623)",
                }}
              >
                <FaShoppingBag size={20} className='text-white' />
              </motion.div>
              <span
                className='text-4xl font-black tracking-tight'
                style={{
                  background:
                    "linear-gradient(90deg, #e94560, #f5a623, #e94560)",
                  backgroundSize: "200%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                GobyMall
              </span>
            </div>
            <p className='text-xs text-slate-400 font-medium tracking-widest uppercase'>
              Go & Buy · Lomé, Togo 🇹🇬
            </p>
          </motion.div>

          {/* Tagline */}
          <div className='flex flex-col gap-1'>
            <motion.h2
              animate={{ letterSpacing: hovered ? "0.05em" : "0em" }}
              transition={{ duration: 0.4 }}
              className='text-white text-xl font-bold leading-snug'
            >
              Le mall en ligne <br />
              <span style={{ color: "#f5a623" }}>de tout le Togo</span>
            </motion.h2>
            <p className='text-slate-400 text-sm leading-relaxed'>
              Des centaines de boutiques, des milliers de produits. Achetez
              local, livré chez vous.
            </p>
          </div>

          {/* Stats */}
          <div className='flex gap-4 w-full justify-center'>
            {[
              { value: "500+", label: "Vendeurs" },
              { value: "10k+", label: "Produits" },
              { value: "⭐ 4.8", label: "Note" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.1 }}
                className='flex flex-col items-center px-3 py-2 rounded-2xl'
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <span className='text-white font-bold text-base'>
                  {stat.value}
                </span>
                <span className='text-slate-400 text-xs'>{stat.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Badges */}
          <div className='flex flex-wrap gap-2 justify-center'>
            {badges.map((badge, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className='flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold'
                style={{
                  background: "rgba(233,69,96,0.15)",
                  border: "1px solid rgba(233,69,96,0.4)",
                  color: "#f5a623",
                }}
              >
                {badge.icon}
                {badge.label}
              </motion.span>
            ))}
          </div>

          {/* CTA Button */}
          <motion.a
            href='#'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className='w-full py-3 rounded-2xl text-white font-bold text-sm tracking-wide shadow-lg relative overflow-hidden'
            style={{ background: "linear-gradient(90deg, #e94560, #f5a623)" }}
          >
            <motion.span
              className='absolute inset-0 opacity-0'
              whileHover={{ opacity: 1 }}
              style={{ background: "rgba(255,255,255,0.15)" }}
            />
            🛒 Découvrir GobyMall
          </motion.a>

          {/* Footer */}
          <p className='text-slate-600 text-xs'>
            gobymall.com · Livraison partout au Togo
          </p>
        </div>

        {/* Shimmer border */}
        <motion.div
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className='absolute inset-0 rounded-3xl pointer-events-none'
          style={{
            border: "1.5px solid rgba(245,166,35,0.5)",
            boxShadow: "0 0 30px rgba(233,69,96,0.3)",
          }}
        />
      </motion.div>
    </div>
  );
}
